// src/services/patientAuditService.js
//
// Centralized Patient Audit Trail service.
//
// All audit events are written to:
//   patient_audits/{patientId}/events/{eventId}
//
// This service is additive: it never reads or mutates patient data outside
// of writing audit events. Callers should fail-soft — audit logging must
// never break the parent save/upload/move flow.

import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getPatientAuditEventsPath } from "./firestorePaths";

/**
 * Return the last 4 characters of an MRN value, safely.
 * Used so we never store the full MRN inside an audit event.
 */
export const getMrnLast4 = (mrn) => {
  if (mrn === null || mrn === undefined) return "";
  const s = String(mrn).trim();
  if (!s) return "";
  return s.length >= 4 ? s.slice(-4) : s;
};

/**
 * Strip a patient object down to a minimal identifier shape suitable for
 * embedding inside an audit event. Never returns the full MRN.
 */
export const maskPatientIdentifiers = (patient) => {
  if (!patient || typeof patient !== "object") return {};
  return {
    patientName: patient.fullName || patient.name || "",
    mrnLast4: getMrnLast4(patient.mrn),
  };
};

// -----------------------------------------------------------------------
// Deep equality helper — only used for diff detection. Tolerates the
// common patient-data shapes (primitives, arrays of primitives, arrays of
// flat objects, and flat objects).
// -----------------------------------------------------------------------
const valuesEqual = (a, b) => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  // Coerce "5" === 5 / true === "true" mismatches that occur when sheet
  // data is stringly-typed while Firestore stores typed values.
  if (typeof a !== typeof b) {
    if (
      (typeof a === "boolean" || typeof b === "boolean") &&
      String(a) === String(b)
    ) {
      return true;
    }
    if (
      (typeof a === "number" || typeof b === "number") &&
      Number(a) === Number(b) &&
      !Number.isNaN(Number(a))
    ) {
      return true;
    }
    return false;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!valuesEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (typeof a === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }

  return String(a) === String(b);
};

/**
 * Build a structured diff between two patient-data snapshots.
 *
 * @param {object} prevData - the previous record (e.g., from Firestore getDoc)
 * @param {object} newData  - the record we are about to write
 * @param {string[]} [fields] - optional whitelist of fields to compare; when
 *   omitted, compares the union of both objects' keys.
 * @returns {Array<{field, from, to}>}
 */
export const buildPatientDiff = (prevData = {}, newData = {}, fields = []) => {
  const changes = [];
  const candidateFields = fields.length
    ? fields
    : Array.from(
        new Set([...Object.keys(prevData || {}), ...Object.keys(newData || {})])
      );

  for (const key of candidateFields) {
    const prev = prevData ? prevData[key] : undefined;
    const next = newData ? newData[key] : undefined;
    if (!valuesEqual(prev, next)) {
      changes.push({
        field: key,
        from: prev === undefined ? null : prev,
        to: next === undefined ? null : next,
      });
    }
  }
  return changes;
};

/**
 * Write a single audit event for a patient.
 *
 * Required:
 *   - patientId
 *   - title (human readable)
 *   - action (machine-friendly slug)
 *
 * Optional (with safe defaults):
 *   - type: "READ" | "WRITE" (default "WRITE")
 *   - severity: "info" | "warning" | "critical" (default "info")
 *   - changes: [{field, from, to}]
 *   - patientName, mrnLast4, source
 *   - actorEmail, actorUid
 *   - metadata (object)
 *
 * Fails soft: logs to console and returns without throwing.
 */
export const logPatientAuditEvent = async ({
  patientId,
  title,
  action,
  type = "WRITE",
  severity = "info",
  changes = [],
  patientName = "",
  mrnLast4 = "",
  source = "",
  actorEmail = "unknown",
  actorUid = null,
  metadata = {},
} = {}) => {
  if (!patientId || !title || !action) {
    // Never throw — just skip if the call site is missing required fields.
    return;
  }
  try {
    const eventsRef = collection(db, getPatientAuditEventsPath(patientId));
    await addDoc(eventsRef, {
      title,
      action,
      patientId,
      patientName,
      mrnLast4,
      source,
      actorEmail,
      actorUid,
      type,
      severity,
      changes,
      metadata,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    // Audit must never break the parent flow.
    console.error("❌ logPatientAuditEvent failed:", err);
  }
};

// Field groupings, exported so the page can build grouped events
// consistently without duplicating field lists across components.
export const PATIENT_PROFILE_FIELDS = [
  "name",
  "dob",
  "phone",
  "email",
  "physicalAddress",
  "emergencyContactName",
  "emergencyContactPhone",
  "callMade",
  "scheduled",
  "dateScheduled",
  "rescreenDate",
  "study",
  "notes",
  "diagnosis",
  "sex",
  "smokingStatus",
  "smokingNote",
];

export const PATIENT_CLINICAL_FIELDS = [
  "eosNA",
  "eosValues",
  "exacNA",
  "exacerbations",
  "ageNA",
  "age",
  "pftNA",
  "pftDate",
  "fev1fvcPre",
  "fev1fvcPost",
  "fev1Pre",
  "fev1Post",
  "fvcPre",
  "fvcPost",
  "revPercent",
  "revMl",
  "methNA",
  "methResult",
  "methDate",
  "fenoNA",
  "fenoPPB",
  "fenoDate",
  "oxygenLitres",
  "oxygenType",
  "oxygenNA",
  "cancerNA",
  "cancerStatus",
  "cancerDate",
  "bmi",
  "ahi",
  "a1c",
  "a1cDate",
  "diabetes",
  "studyEligibility",
  "pftTests",
  "diagnosticScans",
];
